import { InputBaseComponentsPropsOverrides } from "@mui/material";
import { setRef } from "@mui/material/utils";

import Grid from "@mui/material/Grid2";
import { useAutoJumpFocus } from "@sinch/hooks/useAutoJumpFocus";
import { FactoryArg, InputMask } from "imask";
import React, { forwardRef } from "react";
import { MaskedInput } from "./MaskedInput";
import { BankNumberInputValue, DelimiterSchema, SectionSchema } from "./types";
import { isSection, parseStringToFields } from "./utils";

export interface BankNumberBaseInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "value">,
    InputBaseComponentsPropsOverrides {
  schema: (SectionSchema | DelimiterSchema)[];
  onFocusChange: (index: number) => void;
  onChange?: (value: BankNumberInputValue) => void;
  value?: Record<string, string>;
  inputRefs: React.MutableRefObject<(HTMLInputElement | null)[]>;
}

export const BankNumberBaseInput = forwardRef<HTMLInputElement, BankNumberBaseInputProps>(
  ({ disabled, readOnly, value, onChange, schema, onFocusChange, inputRefs }, ref) => {
    const inputsList = schema.filter(isSection);

    // The first section input represents the whole field for the outer ref (InputBase, react-hook-form focus / scroll)
    const handleInputRef = (index: number, element: HTMLInputElement | null) => {
      inputRefs.current[index] = element;
      if (index === 0) {
        setRef(ref, element);
      }
    };

    const { onKeyDown } = useAutoJumpFocus(inputRefs);

    const handleChange = (newValue: string, mask: InputMask<FactoryArg>, index: number) => {
      if (newValue === "") {
        inputRefs?.current[index - 1]?.focus();
      }
      if (newValue.length === inputsList[index].size) {
        inputRefs?.current[index + 1]?.focus();
      }
      onChange?.({ ...(value || {}), [inputsList[index].name]: newValue });
    };

    const handleComplete = (index: number) => inputRefs?.current[index + 1]?.focus();
    const findInputIndex = (item: DelimiterSchema | SectionSchema): number =>
      isSection(item) ? inputsList.indexOf(item) : -1;

    return (
      <Grid
        container
        direction="row"
        onPaste={(event) => {
          const text = event.clipboardData.getData("text");
          onChange?.(parseStringToFields(schema, text));
        }}
        spacing={1}
        sx={{
          alignItems: "stretch",
          pr: 2,
          height: "100%",
        }}
        wrap="nowrap"
      >
        {schema.map((item, i) => (
          <Grid
            key={isSection(item) ? item.name : `${item.title}_${i}`}
            size={isSection(item) && item.flexible ? "grow" : "auto"}
          >
            <MaskedInput
              disabled={disabled}
              handleInputRef={handleInputRef}
              inputIndex={findInputIndex(item)}
              item={item}
              name={isSection(item) ? item.name : undefined}
              onChange={handleChange}
              onComplete={handleComplete}
              onFocus={() => onFocusChange(findInputIndex(item))}
              onKeyDown={onKeyDown}
              readOnly={readOnly}
              value={value?.[isSection(item) ? item.name : ""] ?? undefined}
            />
          </Grid>
        ))}
      </Grid>
    );
  }
);
BankNumberBaseInput.displayName = "BankNumberBaseInput";
